import { useContext } from 'react'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { PageHeader } from 'app/components/ui/page-header'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'app/components/ui/table'
import type { UseOpsExtractionDashboardReturn } from './useOpsExtractionDashboard'
import { PAGE_GUTTER, PageGutterContext } from 'app/components/layout/PageScroll'
import { cn } from 'app/lib/utils'

export type OpsExtractionDashboardShadcnComponentProps = UseOpsExtractionDashboardReturn

export function OpsExtractionDashboardShadcnComponent({
  workspaceId,
  shadowStats,
  promptVersions,
  testCallId,
  setTestCallId,
  isRunning,
  extractionData,
  onRunExtraction,
}: OpsExtractionDashboardShadcnComponentProps) {
  const gutter = useContext(PageGutterContext)
  return (
    <div className={cn(`flex h-full flex-col pt-8`, PAGE_GUTTER[gutter])}>
      <div className="mb-6 flex items-center justify-between">
        <PageHeader title="LangGraph Extraction Dashboard" />
        {workspaceId && (
          <span className="rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-700">
            Filtered: {workspaceId.slice(0, 8)}...
          </span>
        )}
      </div>

      {/* Test Extraction */}
      <div className="mb-8 rounded-lg border bg-surface p-4 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold">Test Extraction</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Run the LangGraph extraction pipeline on a specific call (bypasses feature toggles).
        </p>
        <div className="flex gap-3">
          <Input
            value={testCallId}
            onChange={(e) => setTestCallId(e.target.value)}
            placeholder="Enter Call ID..."
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === `Enter`) onRunExtraction()
            }}
          />
          <Button onClick={onRunExtraction} disabled={isRunning || !testCallId.trim()}>
            {isRunning ? `Running...` : `Run Extraction`}
          </Button>
        </div>
        {extractionData && (
          <div className={`mt-4 rounded-md p-3 text-sm ${extractionData.success ? `bg-green-50` : `bg-red-50`}`}>
            {extractionData.success ? (
              <div>
                <div className="mb-2 font-medium text-green-700">
                  Extraction completed in {extractionData.totalTime}ms
                </div>
                {extractionData.errors?.length > 0 && (
                  <div className="mb-2 text-yellow-700">
                    Warnings: {extractionData.errors.map((e: any) => e.error).join(`, `)}
                  </div>
                )}
                <pre className="max-h-64 overflow-auto rounded bg-surface-subtle p-2 text-xs">
                  {JSON.stringify(extractionData.output, null, 2)}
                </pre>
              </div>
            ) : (
              <div className="text-red-700">{extractionData.error}</div>
            )}
          </div>
        )}
      </div>

      {/* Shadow Mode Stats */}
      <div className="mb-8">
        <h2 className="mb-4 text-lg font-semibold">Shadow Mode Statistics</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg border bg-surface p-4 shadow-sm">
            <div className="text-3xl font-bold text-blue-600">{shadowStats.total}</div>
            <div className="text-sm text-muted-foreground">Total Shadow Extractions</div>
          </div>
          <div className="rounded-lg border bg-surface p-4 shadow-sm">
            <div className="text-3xl font-bold text-green-600">{shadowStats.last24h}</div>
            <div className="text-sm text-muted-foreground">Last 24 Hours</div>
          </div>
          <div className="rounded-lg border bg-surface p-4 shadow-sm">
            <div className="text-3xl font-bold text-purple-600">
              {shadowStats.avgMatchScore > 0 ? `${(shadowStats.avgMatchScore * 100).toFixed(1)}%` : `N/A`}
            </div>
            <div className="text-sm text-muted-foreground">Avg Match Score</div>
          </div>
        </div>
      </div>

      {/* Workspace Breakdown */}
      {shadowStats.byWorkspace.length > 0 && (
        <div className="mb-8">
          <h2 className="mb-4 text-lg font-semibold">By Workspace</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Workspace ID</TableHead>
                <TableHead>Count</TableHead>
                <TableHead>Avg Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shadowStats.byWorkspace.map((ws) => (
                <TableRow key={ws.workspaceId}>
                  <TableCell className="font-medium">{ws.workspaceId}</TableCell>
                  <TableCell className="text-muted-foreground">{ws.count}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {ws.avgScore > 0 ? `${(ws.avgScore * 100).toFixed(1)}%` : `N/A`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Prompt Versions */}
      <div className="mb-8">
        <h2 className="mb-4 text-lg font-semibold">Prompt Versions (from code)</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
          {promptVersions.map(({ stage, version }) => (
            <div key={stage} className="rounded-lg border bg-surface p-4 shadow-sm">
              <div className="mb-1 text-sm font-medium capitalize">{stage}</div>
              <div className="text-xs text-muted-foreground">v{version}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
